import React, { useState, useEffect } from 'react';
import { adminConfigAPI } from '../../services/adminAPI';
import { toast } from 'react-toastify';

const CATEGORIES = [
  { key: '', label: 'All' },
  { key: 'subscription', label: '💳 Subscription' },
  { key: 'general', label: '⚙️ General' },
  { key: 'features', label: '🚀 Features' },
  { key: 'email', label: '📧 Email' },
];

const SystemConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [newConfig, setNewConfig] = useState({ key: '', value: '', value_type: 'string', label: '', description: '', category: 'general' });

  const load = async (cat = '') => {
    setLoading(true);
    try {
      const params = cat ? { category: cat } : {};
      const res = await adminConfigAPI.getAll(params);
      const data = res.data.configs || [];
      setConfigs(data);
      const vals = {};
      data.forEach(c => { vals[c.key] = c.value; });
      setEditValues(vals);
    } catch {
      toast.error('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(activeCategory); }, [activeCategory]);

  const handleSave = async (key) => {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await adminConfigAPI.update(key, { value: editValues[key] });
      toast.success(`"${key}" updated`);
      setConfigs(c => c.map(cfg => cfg.key === key ? { ...cfg, value: editValues[key] } : cfg));
    } catch {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Delete config key "${key}"? This cannot be undone.`)) return;
    try {
      await adminConfigAPI.delete(key);
      toast.success('Config deleted');
      setConfigs(c => c.filter(cfg => cfg.key !== key));
    } catch {
      toast.error('Failed to delete config');
    }
  };

  const handleCreate = async () => {
    if (!newConfig.key || !newConfig.label) { toast.error('Key and Label are required'); return; }
    try {
      const res = await adminConfigAPI.create(newConfig);
      toast.success('Config created');
      setConfigs(c => [...c, res.data.config]);
      setEditValues(v => ({ ...v, [res.data.config.key]: res.data.config.value }));
      setShowCreate(false);
      setNewConfig({ key: '', value: '', value_type: 'string', label: '', description: '', category: 'general' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create config');
    }
  };

  const grouped = configs.reduce((acc, cfg) => {
    const cat = cfg.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(cfg);
    return acc;
  }, {});

  return (
    <div style={s.page}>
      <div style={s.topBar}>
        <div>
          <h1 style={s.heading}>⚙️ System Configuration</h1>
          <p style={s.subheading}>Manage all application-wide settings in one place</p>
        </div>
        <button style={s.createBtn} onClick={() => setShowCreate(true)}>+ New Config</button>
      </div>

      {/* Category tabs */}
      <div style={s.tabs}>
        {CATEGORIES.map(cat => (
          <button
            key={cat.key}
            style={{ ...s.tab, ...(activeCategory === cat.key ? s.tabActive : {}) }}
            onClick={() => setActiveCategory(cat.key)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={s.loader}>Loading configurations…</div>
      ) : (
        Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} style={s.section}>
            <h3 style={s.sectionTitle}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</h3>
            <div style={s.table}>
              <div style={s.tableHeader}>
                <span style={{ flex: 2 }}>Setting</span>
                <span style={{ flex: 2 }}>Value</span>
                <span style={{ flex: 1 }}>Actions</span>
              </div>
              {items.map(cfg => (
                <div key={cfg.key} style={s.tableRow}>
                  <div style={{ flex: 2 }}>
                    <div style={s.cfgLabel}>{cfg.label}</div>
                    <div style={s.cfgKey}>{cfg.key}</div>
                    {cfg.description && <div style={s.cfgDesc}>{cfg.description}</div>}
                  </div>
                  <div style={{ flex: 2 }}>
                    {cfg.value_type === 'boolean' ? (
                      <select
                        style={s.input}
                        value={editValues[cfg.key] ?? cfg.value}
                        onChange={e => setEditValues(v => ({ ...v, [cfg.key]: e.target.value }))}
                      >
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </select>
                    ) : (
                      <input
                        style={s.input}
                        type={cfg.value_type === 'number' ? 'number' : 'text'}
                        value={editValues[cfg.key] ?? cfg.value ?? ''}
                        onChange={e => setEditValues(v => ({ ...v, [cfg.key]: e.target.value }))}
                        placeholder={cfg.value_type === 'json' ? 'JSON value' : 'Value'}
                      />
                    )}
                    <div style={s.typeTag}>{cfg.value_type}</div>
                  </div>
                  <div style={{ flex: 1, display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      style={{ ...s.saveBtn, opacity: saving[cfg.key] ? 0.6 : 1 }}
                      onClick={() => handleSave(cfg.key)}
                      disabled={saving[cfg.key]}
                    >
                      {saving[cfg.key] ? '…' : 'Save'}
                    </button>
                    <button style={s.deleteBtn} onClick={() => handleDelete(cfg.key)}>🗑</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Create Modal */}
      {showCreate && (
        <div style={s.overlay} onClick={() => setShowCreate(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: '16px' }}>Create New Config</h3>
            {[
              { field: 'key', label: 'Key *', placeholder: 'e.g. my_feature_flag' },
              { field: 'label', label: 'Label *', placeholder: 'Human-readable name' },
              { field: 'value', label: 'Default Value', placeholder: 'Initial value' },
              { field: 'description', label: 'Description', placeholder: 'Optional description' },
            ].map(({ field, label, placeholder }) => (
              <div key={field} style={{ marginBottom: '12px' }}>
                <label style={s.formLabel}>{label}</label>
                <input
                  style={s.input}
                  value={newConfig[field]}
                  onChange={e => setNewConfig(n => ({ ...n, [field]: e.target.value }))}
                  placeholder={placeholder}
                />
              </div>
            ))}
            <div style={{ marginBottom: '12px' }}>
              <label style={s.formLabel}>Type</label>
              <select style={s.input} value={newConfig.value_type}
                onChange={e => setNewConfig(n => ({ ...n, value_type: e.target.value }))}>
                {['string', 'number', 'boolean', 'json'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={s.formLabel}>Category</label>
              <select style={s.input} value={newConfig.category}
                onChange={e => setNewConfig(n => ({ ...n, category: e.target.value }))}>
                {['general', 'subscription', 'features', 'email'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={s.saveBtn} onClick={handleCreate}>Create</button>
              <button style={{ ...s.deleteBtn, padding: '8px 16px' }} onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  page: { padding: '24px', maxWidth: '1100px' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' },
  heading: { margin: 0, fontSize: '24px', fontWeight: 700, color: '#1f2937' },
  subheading: { margin: '4px 0 0', color: '#6b7280', fontSize: '14px' },
  createBtn: { background: '#6c63ff', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', cursor: 'pointer', fontWeight: 600 },
  tabs: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  tab: { padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: '20px', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: '#374151' },
  tabActive: { background: '#6c63ff', color: '#fff', border: '1px solid #6c63ff' },
  loader: { textAlign: 'center', color: '#888', padding: '48px' },
  section: { marginBottom: '28px' },
  sectionTitle: { fontSize: '15px', fontWeight: 700, color: '#6c63ff', marginBottom: '10px', textTransform: 'capitalize' },
  table: { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' },
  tableHeader: { display: 'flex', padding: '12px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: '12px', fontWeight: 600, color: '#6b7280', gap: '12px' },
  tableRow: { display: 'flex', padding: '14px 16px', borderBottom: '1px solid #f3f4f6', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  cfgLabel: { fontWeight: 600, fontSize: '14px', color: '#1f2937' },
  cfgKey: { fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace', marginTop: '2px' },
  cfgDesc: { fontSize: '12px', color: '#6b7280', marginTop: '4px' },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', outline: 'none' },
  typeTag: { display: 'inline-block', fontSize: '10px', background: '#f3f4f6', color: '#6b7280', borderRadius: '4px', padding: '2px 6px', marginTop: '4px' },
  saveBtn: { background: '#6c63ff', color: '#fff', border: 'none', borderRadius: '8px', padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' },
  deleteBtn: { background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px', padding: '7px 10px', cursor: 'pointer', fontSize: '14px' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px' },
  modal: { background: '#fff', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '480px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' },
  formLabel: { display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' },
};

export default SystemConfig;
